import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, PhoneCall } from 'lucide-react';

// WhatsApp Configuration
const WHATSAPP_NUMBER = '4917680151197';
const WHATSAPP_MESSAGE = 'Hallo Erste Hilfe Bottrop, ich habe eine Frage zu einem Kurstermin.';

const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg className={`${className} text-white`} fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Function to handle navigation with scroll to top
  const handleNavClick = (path, e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Function to handle logo click
  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      {/* Sticky Navigation Container */}
      <div className="sticky top-0 z-50">
        {/* White Header Section - Full Width Background */}
        <div className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Mobile Header - KOMPAKT */}
            <div className="lg:hidden">
              <div className="flex justify-between items-center py-1.5 gap-2">
                {/* Logo - kleiner */}
                <a href="/" onClick={handleLogoClick} className="flex-shrink-0">
                  <img 
                    src="/images/logo.png" 
                    alt="Erste Hilfe Bottrop - Aktiv helfen" 
                    className="h-9 sm:h-10 w-auto"
                  />
                </a>
                
                {/* Phone Numbers - Centered, Stacked - Fixed for Android */}
                <div className="flex flex-col items-center justify-center gap-0 px-1 min-w-0 flex-1">
                  <a 
                    href="tel:017680151197" 
                    className="flex items-center gap-1 text-[#1e3a5f] hover:text-red-600 transition-colors text-[10px] font-medium whitespace-nowrap"
                  >
                    <Phone className="w-3 h-3 text-red-600 flex-shrink-0" />
                    <span className="whitespace-nowrap">0176 8015 1197</span>
                  </a>
                  <a 
                    href="tel:020419878666" 
                    className="flex items-center gap-1 text-[#1e3a5f] hover:text-red-600 transition-colors text-[10px] font-medium whitespace-nowrap"
                  >
                    <PhoneCall className="w-3 h-3 text-red-600 flex-shrink-0" />
                    <span className="whitespace-nowrap">02041 9878 666</span>
                  </a>
                </div>
                
                {/* WhatsApp + Menu - kleiner */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md bg-[#25D366] hover:bg-[#20BD5A] text-white transition-colors"
                    aria-label="WhatsApp kontaktieren"
                    data-testid="whatsapp-header-btn-mobile"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                  </a>
                  <button
                    className="p-1.5 rounded-md bg-[#1e3a5f] text-white"
                    onClick={() => setMobileMenuOpen(true)}
                    aria-label="Menü öffnen"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop: Logo + Phone Numbers + WhatsApp - KOMPAKT */}
            <div className="hidden lg:flex justify-between items-center py-2">
              {/* Logo - kleiner */}
              <a href="/" onClick={handleLogoClick}>
                <img 
                  src="/images/logo.png" 
                  alt="Erste Hilfe Bottrop - Aktiv helfen" 
                  className="h-12 w-auto"
                />
              </a>
              
              {/* Phone Numbers + WhatsApp */}
              <div className="flex items-center gap-4">
                {/* Phone Numbers - kleinere Schrift */}
                <div className="flex items-center gap-3 text-xs">
                  <a 
                    href="tel:017680151197" 
                    className="flex items-center gap-1.5 text-[#1e3a5f] hover:text-red-600 transition-colors font-medium"
                  >
                    <Phone className="w-4 h-4 text-red-600" />
                    <span>0176 8015 1197</span>
                  </a>
                  <span className="text-slate-300">|</span>
                  <a 
                    href="tel:020419878666" 
                    className="flex items-center gap-1.5 text-[#1e3a5f] hover:text-red-600 transition-colors font-medium"
                  >
                    <PhoneCall className="w-4 h-4 text-red-600" />
                    <span>02041 9878 666</span>
                  </a>
                </div>
                
                {/* WhatsApp Button - kleiner */}
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs font-medium transition-colors"
                  aria-label="WhatsApp kontaktieren"
                  data-testid="whatsapp-header-btn-desktop"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Desktop Navigation - Blue Bar - Contained width matching content */}
        <div className="hidden lg:block">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3 px-8 bg-[#1e3a5f]">
              {/* Erste Hilfe (Hauptseite) */}
              <a
                href="/"
                onClick={(e) => handleNavClick('/', e)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded ${
                  location.pathname === '/'
                    ? 'text-white bg-white/10' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Erste Hilfe
              </a>
              
              {/* Betriebliche Erste Hilfe */}
              <a
                href="/betriebliche-erste-hilfe"
                onClick={(e) => handleNavClick('/betriebliche-erste-hilfe', e)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded ${
                  location.pathname === '/betriebliche-erste-hilfe' 
                    ? 'text-white bg-white/10' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Kurse für Betriebe (DGUV)
              </a>
              
              {/* Inhouse-Kurse für Betriebe */}
              <a
                href="/inhouse-kurse"
                onClick={(e) => handleNavClick('/inhouse-kurse', e)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded ${
                  location.pathname === '/inhouse-kurse' 
                    ? 'text-white bg-white/10' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Inhouse-Kurse für Betriebe
              </a>
              
              {/* Termine */}
              <a
                href="/termine"
                onClick={(e) => handleNavClick('/termine', e)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded ${
                  location.pathname === '/termine' 
                    ? 'text-white bg-white/10' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Termine
              </a>
              
              {/* Ersatzbescheinigung */}
              <a
                href="/ersatzbescheinigung"
                onClick={(e) => handleNavClick('/ersatzbescheinigung', e)}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded ${
                  location.pathname === '/ersatzbescheinigung' 
                    ? 'text-white bg-white/10' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                Ersatzbescheinigung
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Slide-in */}
      <div className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-[#1e3a5f] z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex justify-end p-4">
          <button
            className="p-2 text-white hover:bg-white/10 rounded-lg"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Menü schließen"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="px-2">
          {/* Erste Hilfe (Hauptseite) */}
          <div>
            <a
              href="/"
              className={`block px-4 py-4 text-lg font-medium ${
                location.pathname === '/'
                  ? 'text-white bg-white/10' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
              onClick={(e) => handleNavClick('/', e)}
            >
              Erste Hilfe
            </a>
            <div className="border-b border-white/20 mx-4" />
          </div>
          
          {/* Betriebliche Erste Hilfe */}
          <div>
            <a
              href="/betriebliche-erste-hilfe"
              className={`block px-4 py-4 text-lg font-medium ${
                location.pathname === '/betriebliche-erste-hilfe' 
                  ? 'text-white bg-white/10' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
              onClick={(e) => handleNavClick('/betriebliche-erste-hilfe', e)}
            >
              Erste-Hilfe-Kurse für Betriebe (DGUV)
            </a>
            <div className="border-b border-white/20 mx-4" />
          </div>
          
          {/* Inhouse-Kurse für Betriebe */}
          <div>
            <a
              href="/inhouse-kurse"
              className={`block px-4 py-4 text-lg font-medium ${
                location.pathname === '/inhouse-kurse' 
                  ? 'text-white bg-white/10' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
              onClick={(e) => handleNavClick('/inhouse-kurse', e)}
            >
              Inhouse-Kurse für Betriebe
            </a>
            <div className="border-b border-white/20 mx-4" />
          </div>
          
          {/* Termine */}
          <div>
            <a
              href="/termine"
              className={`block px-4 py-4 text-lg font-medium ${
                location.pathname === '/termine' 
                  ? 'text-white bg-white/10' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
              onClick={(e) => handleNavClick('/termine', e)}
            >
              Termine
            </a>
            <div className="border-b border-white/20 mx-4" />
          </div>
          
          {/* Ersatzbescheinigung */}
          <div>
            <a
              href="/ersatzbescheinigung"
              className={`block px-4 py-4 text-lg font-medium ${
                location.pathname === '/ersatzbescheinigung' 
                  ? 'text-white bg-white/10' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
              onClick={(e) => handleNavClick('/ersatzbescheinigung', e)}
            >
              Ersatzbescheinigung
            </a>
          </div>
        </nav>
      </div>
    </>
  );
};
